// frontend/src/pages/admin/Dashboard.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../../config/axios';
import { useSocket } from '../../contexts/SocketContext';
import {
    Users, Trophy, Swords, CreditCard,
    RefreshCw, ShieldBan, MailWarning, Gamepad2,
    CircleCheck, Clock, UserCheck, Wifi, WifiOff,
    TrendingUp, Activity, Globe, Radio
} from 'lucide-react';
import {
    LineChart, Line, BarChart, Bar, AreaChart, Area,
    XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid
} from 'recharts';

const REFRESH_INTERVAL = 30_000; // 30 segundos

// ─── Tarjeta de estadística principal ────────────────────────────────────────
const StatCard = ({ icon: Icon, label, value, color = '#2284DF', sub }) => (
    <div
        className="rounded-xl p-5 flex flex-col gap-3"
        style={{ backgroundColor: '#0E2238' }}
    >
        <div className="flex items-center justify-between">
            <span className="text-sm font-semibold uppercase tracking-wide text-gray-400">{label}</span>
            <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${color}22` }}>
                <Icon className="w-5 h-5" style={{ color }} />
            </div>
        </div>
        <p className="text-4xl font-bold text-white">{value ?? '—'}</p>
        {sub && (
            <div className="flex flex-wrap gap-x-4 gap-y-1">
                {sub.map((s, i) => (
                    <span key={i} className="text-xs text-gray-400 flex items-center gap-1">
                        <span className="w-1.5 h-1.5 rounded-full inline-block" style={{ backgroundColor: s.color || '#9ca3af' }} />
                        {s.label}: <span className="font-semibold text-gray-300 ml-0.5">{s.value}</span>
                    </span>
                ))}
            </div>
        )}
    </div>
);

// ─── Badge de estado de torneo ─────────────────────────────────────────────
const StatusBadge = ({ status }) => {
    const map = {
        REGISTERING: { label: 'Registro', cls: 'bg-blue-900 text-blue-300' },
        IN_PROGRESS: { label: 'En Curso', cls: 'bg-green-900 text-green-300' },
        COMPLETED:   { label: 'Finalizado', cls: 'bg-gray-700 text-gray-300' },
    };
    const { label, cls } = map[status] || { label: status, cls: 'bg-gray-700 text-gray-300' };
    return (
        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${cls}`}>{label}</span>
    );
};

// ─── Tooltip oscuro para recharts ────────────────────────────────────────────
const DarkTooltip = ({ active, payload, label, formatter }) => {
    if (!active || !payload?.length) return null;
    const val = formatter ? formatter(payload[0].value) : payload[0].value;
    return (
        <div className="rounded-lg px-3 py-2 text-sm shadow-xl"
             style={{ backgroundColor: '#0A1929', border: '1px solid #1e3a5f' }}>
            <p className="text-gray-400 text-xs mb-0.5">{label}</p>
            <p className="font-semibold text-white">{val}</p>
        </div>
    );
};

// ─── Tarjeta con gráfico mini (line / bar / area) ─────────────────────────────
const ChartCard = ({ title, icon: Icon, iconColor, total, subtitle, color, data = [], type = 'line', formatter, loading }) => {
    const GRID = '#1e3a5f', TICK = '#6b7280';
    return (
        <div className="rounded-xl p-5 flex flex-col gap-3" style={{ backgroundColor: '#0E2238' }}>
            <div className="flex items-center justify-between">
                <span className="flex items-center gap-1.5 text-sm font-semibold text-gray-400">
                    {Icon && <Icon className="w-4 h-4" style={{ color: iconColor }} />}
                    {title}
                </span>
                <span className="text-2xl font-bold text-white">
                    {loading ? '—' : (total ?? '—')}
                </span>
            </div>
            {subtitle && <p className="text-xs text-gray-500 -mt-2">{subtitle}</p>}
            <div className="h-[130px]">
                {loading ? (
                    <div className="h-full flex items-center justify-center">
                        <RefreshCw className="w-5 h-5 animate-spin text-gray-600" />
                    </div>
                ) : (
                    <ResponsiveContainer width="100%" height="100%">
                        {type === 'bar' ? (
                            <BarChart data={data} margin={{ top: 4, right: 0, left: -30, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke={GRID} vertical={false} />
                                <XAxis dataKey="shortLabel" tick={{ fill: TICK, fontSize: 10 }} axisLine={false} tickLine={false} />
                                <YAxis tick={{ fill: TICK, fontSize: 10 }} axisLine={false} tickLine={false} allowDecimals={false} />
                                <Tooltip content={<DarkTooltip formatter={formatter} />} cursor={{ fill: '#ffffff08' }} />
                                <Bar dataKey="value" fill={color} radius={[3, 3, 0, 0]} />
                            </BarChart>
                        ) : type === 'area' ? (
                            <AreaChart data={data} margin={{ top: 4, right: 0, left: -30, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke={GRID} vertical={false} />
                                <XAxis dataKey="shortLabel" tick={{ fill: TICK, fontSize: 10 }} axisLine={false} tickLine={false} />
                                <YAxis tick={{ fill: TICK, fontSize: 10 }} axisLine={false} tickLine={false} allowDecimals={false} />
                                <Tooltip content={<DarkTooltip formatter={formatter} />} cursor={{ stroke: '#ffffff20' }} />
                                <Area type="monotone" dataKey="value" stroke={color} fill={`${color}28`} strokeWidth={2} dot={false} />
                            </AreaChart>
                        ) : (
                            <LineChart data={data} margin={{ top: 4, right: 0, left: -30, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke={GRID} vertical={false} />
                                <XAxis dataKey="shortLabel" tick={{ fill: TICK, fontSize: 10 }} axisLine={false} tickLine={false} />
                                <YAxis tick={{ fill: TICK, fontSize: 10 }} axisLine={false} tickLine={false} allowDecimals={false} />
                                <Tooltip content={<DarkTooltip formatter={formatter} />} cursor={{ stroke: '#ffffff20' }} />
                                <Line type="monotone" dataKey="value" stroke={color} strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
                            </LineChart>
                        )}
                    </ResponsiveContainer>
                )}
            </div>
        </div>
    );
};

// ─── Dashboard ────────────────────────────────────────────────────────────────
const AdminDashboard = () => {
    const navigate = useNavigate();
    const { socket, isConnected } = useSocket();
    const [data, setData] = useState(null);
    // Estado separado para la cola: solo el socket puede escribirlo,
    // así el REST nunca lo sobreescribe con 0.
    const [queueStats, setQueueStats] = useState(null);
    const [metrics, setMetrics] = useState(null);
    const [metricsLoading, setMetricsLoading] = useState(true);
    // Conexiones activas: se actualiza en tiempo real vía socket
    const [liveConnections, setLiveConnections] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState('');
    const [lastUpdated, setLastUpdated] = useState(null);
    const [queueLastUpdated, setQueueLastUpdated] = useState(null);

    const fetchMetrics = useCallback(async () => {
        try {
            const res = await axios.get('/admin/metrics');
            if (res.data.success) {
                setMetrics(res.data.data);
                setLiveConnections(res.data.data.connections);
            }
        } catch (err) {
            console.error('Error al obtener métricas:', err);
        } finally {
            setMetricsLoading(false);
        }
    }, []);

    const fetchStats = useCallback(async (isManual = false) => {
        if (isManual) setRefreshing(true);
        try {
            const res = await axios.get('/admin/stats');
            if (res.data.success) {
                setData(res.data.data);
                setLastUpdated(new Date());
                setError('');
            } else {
                setError('No se pudieron cargar las estadísticas.');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Error al conectar con el servidor.');
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, []);

    // Carga inicial
    useEffect(() => {
        fetchStats();
        fetchMetrics();
    }, [fetchStats, fetchMetrics]);

    // Auto-refresh: stats cada 30 s, métricas cada 5 min
    useEffect(() => {
        const statsId = setInterval(() => fetchStats(), REFRESH_INTERVAL);
        const metricsId = setInterval(() => fetchMetrics(), 5 * 60_000);
        return () => { clearInterval(statsId); clearInterval(metricsId); };
    }, [fetchStats, fetchMetrics]);

    // Actualización en tiempo real + carga inicial de la cola
    // Escucha directamente el evento 'connect' del socket para evitar
    // problemas de timing con el estado React (isConnected).
    useEffect(() => {
        if (!socket) return;

        const handleQueueUpdate = (stats) => {
            setQueueStats(stats);
            setQueueLastUpdated(new Date());
        };

        const handleConnect = () => {
            socket.emit('request_queue_stats');
        };

        const handleConnectionsUpdate = (connStats) => {
            setLiveConnections(connStats);
        };

        socket.on('queue:stats_broadcast', handleQueueUpdate);
        socket.on('connect', handleConnect);
        socket.on('admin:connections_update', handleConnectionsUpdate);

        // Si el socket ya estaba conectado cuando el componente montó, pedir stats ahora mismo
        if (socket.connected) {
            socket.emit('request_queue_stats');
        }

        return () => {
            socket.off('queue:stats_broadcast', handleQueueUpdate);
            socket.off('connect', handleConnect);
            socket.off('admin:connections_update', handleConnectionsUpdate);
        };
    }, [socket]);

    const formatTime = (date) =>
        date ? date.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '—';

    const formatDate = (iso) =>
        new Date(iso).toLocaleString('es-AR', {
            day: '2-digit', month: 'short',
            hour: '2-digit', minute: '2-digit'
        });

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <RefreshCw className="w-8 h-8 animate-spin text-gray-500" />
            </div>
        );
    }

    const { users, tournaments, transactions, matches, recentUsers, activeTournaments } = data || {};
    // queueStats viene exclusivamente del socket (nunca del REST) para evitar race conditions
    const queue = queueStats;
    const playersInQueue = queue
        ? Object.values(queue).reduce((acc, q) => acc + (q?.currentPlayers ?? 0), 0)
        : null;

    return (
        <div className="space-y-6">

            {/* ── Encabezado ─────────────────────────────────────────────── */}
            <div className="flex items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-white">Panel de Administración</h1>
                    <div className="flex items-center gap-3 mt-1">
                        {lastUpdated && (
                            <p className="text-xs text-gray-500">
                                Stats: {formatTime(lastUpdated)} · cada 30 s
                            </p>
                        )}
                        <span className={`flex items-center gap-1 text-xs font-medium ${isConnected ? 'text-green-400' : 'text-red-400'}`}>
                            {isConnected
                                ? <><Wifi className="w-3 h-3" /> Cola en vivo{queueLastUpdated ? ` · ${formatTime(queueLastUpdated)}` : ''}</>
                                : <><WifiOff className="w-3 h-3" /> Sin conexión WS</>
                            }
                        </span>
                    </div>
                </div>
                <button
                    onClick={() => fetchStats(true)}
                    disabled={refreshing}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white transition-colors shrink-0"
                    style={{ backgroundColor: '#122436' }}
                    onMouseEnter={e => e.currentTarget.style.backgroundColor = '#1a3a5c'}
                    onMouseLeave={e => e.currentTarget.style.backgroundColor = '#122436'}
                >
                    <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                    {refreshing ? 'Actualizando...' : 'Actualizar'}
                </button>
            </div>

            {/* ── Error ──────────────────────────────────────────────────── */}
            {error && (
                <div className="p-4 rounded-lg text-red-400 text-sm" style={{ backgroundColor: '#2d1a1a' }}>
                    {error}
                </div>
            )}

            {/* ── Tarjetas principales ───────────────────────────────────── */}
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
                <StatCard
                    icon={Users}
                    label="Usuarios"
                    value={users?.total}
                    color="#2284DF"
                    sub={[
                        { label: 'Activos',       value: users?.active,    color: '#22c55e' },
                        { label: 'Baneados',      value: users?.banned,    color: '#ef4444' },
                        { label: 'Sin verificar', value: users?.unverified, color: '#f59e0b' },
                    ]}
                />
                <StatCard
                    icon={Trophy}
                    label="Torneos"
                    value={tournaments?.total}
                    color="#FCE503"
                    sub={[
                        { label: 'En registro', value: tournaments?.registering, color: '#60a5fa' },
                        { label: 'En curso',    value: tournaments?.inProgress,  color: '#22c55e' },
                        { label: 'Finalizados', value: tournaments?.completed,   color: '#9ca3af' },
                    ]}
                />
                <StatCard
                    icon={Swords}
                    label="Matches Activos"
                    value={matches?.active}
                    color="#a855f7"
                    sub={
                        playersInQueue !== null
                            ? [{ label: 'En cola', value: playersInQueue, color: '#c084fc' }]
                            : []
                    }
                />
                <StatCard
                    icon={CreditCard}
                    label="Transacciones Hoy"
                    value={transactions?.todayCount}
                    color="#22c55e"
                    sub={[
                        {
                            label: 'Depósitos',
                            value: `${transactions?.todayDeposits ?? 0} tickets`,
                            color: '#4ade80'
                        },
                    ]}
                />
            </div>

            {/* ── Tablas ────────────────────────────────────────────────── */}
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">

                {/* Usuarios recientes */}
                <div className="rounded-xl overflow-hidden" style={{ backgroundColor: '#0E2238' }}>
                    <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: '#122436' }}>
                        <h2 className="font-semibold text-white flex items-center gap-2">
                            <UserCheck className="w-4 h-4" style={{ color: '#2284DF' }} />
                            Usuarios Recientes
                        </h2>
                        <button
                            onClick={() => navigate('/admin/users')}
                            className="text-xs font-semibold transition-colors"
                            style={{ color: '#2284DF' }}
                            onMouseEnter={e => e.currentTarget.style.color = '#FCE503'}
                            onMouseLeave={e => e.currentTarget.style.color = '#2284DF'}
                        >
                            Ver todos →
                        </button>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="min-w-full text-sm">
                            <thead style={{ backgroundColor: '#0A1929' }}>
                                <tr>
                                    {['Usuario', 'Tickets', 'Registro', 'Estado'].map(h => (
                                        <th key={h} className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {(recentUsers || []).map((u) => (
                                    <tr key={u.id} className="border-t" style={{ borderColor: '#122436' }}>
                                        <td className="px-4 py-3">
                                            <div className="flex items-center gap-2">
                                                <div
                                                    className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-white shrink-0"
                                                    style={{ backgroundColor: '#2284DF' }}
                                                >
                                                    {u.username[0].toUpperCase()}
                                                </div>
                                                <div>
                                                    <p className="font-medium text-white leading-tight">{u.username}</p>
                                                    <p className="text-xs text-gray-500">{u.email}</p>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 text-gray-300">{u.tickets}</td>
                                        <td className="px-4 py-3 text-gray-400 whitespace-nowrap">{formatDate(u.createdAt)}</td>
                                        <td className="px-4 py-3">
                                            {u.isBanned ? (
                                                <span className="flex items-center gap-1 text-xs text-red-400">
                                                    <ShieldBan className="w-3.5 h-3.5" /> Baneado
                                                </span>
                                            ) : !u.emailVerified ? (
                                                <span className="flex items-center gap-1 text-xs text-yellow-400">
                                                    <MailWarning className="w-3.5 h-3.5" /> Sin verificar
                                                </span>
                                            ) : (
                                                <span className="flex items-center gap-1 text-xs text-green-400">
                                                    <CircleCheck className="w-3.5 h-3.5" /> Activo
                                                </span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                                {(recentUsers || []).length === 0 && (
                                    <tr>
                                        <td colSpan={4} className="px-4 py-6 text-center text-gray-500">Sin usuarios aún</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Torneos activos */}
                <div className="rounded-xl overflow-hidden" style={{ backgroundColor: '#0E2238' }}>
                    <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: '#122436' }}>
                        <h2 className="font-semibold text-white flex items-center gap-2">
                            <Gamepad2 className="w-4 h-4" style={{ color: '#FCE503' }} />
                            Torneos Activos
                        </h2>
                        <button
                            onClick={() => navigate('/admin/tournaments')}
                            className="text-xs font-semibold transition-colors"
                            style={{ color: '#2284DF' }}
                            onMouseEnter={e => e.currentTarget.style.color = '#FCE503'}
                            onMouseLeave={e => e.currentTarget.style.color = '#2284DF'}
                        >
                            Ver todos →
                        </button>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="min-w-full text-sm">
                            <thead style={{ backgroundColor: '#0A1929' }}>
                                <tr>
                                    {['Torneo', 'Jugadores', 'Inicio', 'Estado'].map(h => (
                                        <th key={h} className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {(activeTournaments || []).map((t) => (
                                    <tr key={t.id} className="border-t" style={{ borderColor: '#122436' }}>
                                        <td className="px-4 py-3">
                                            <p className="font-medium text-white leading-tight">{t.name}</p>
                                            <p className="text-xs text-gray-500">{t.game?.name} · {t.entryFee} tickets</p>
                                        </td>
                                        <td className="px-4 py-3 text-gray-300 whitespace-nowrap">
                                            {t._count?.players ?? 0}
                                            <span className="text-gray-500">/{t.maxPlayers}</span>
                                        </td>
                                        <td className="px-4 py-3 text-gray-400 whitespace-nowrap">
                                            <span className="flex items-center gap-1">
                                                <Clock className="w-3 h-3" />
                                                {formatDate(t.startTime)}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3">
                                            <StatusBadge status={t.status} />
                                        </td>
                                    </tr>
                                ))}
                                {(activeTournaments || []).length === 0 && (
                                    <tr>
                                        <td colSpan={4} className="px-4 py-6 text-center text-gray-500">Sin torneos activos</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {/* ── Métricas ─────────────────────────────────────────────── */}
            <div className="space-y-4">
                <h2 className="font-semibold text-white flex items-center gap-2">
                    <TrendingUp className="w-4 h-4" style={{ color: '#2284DF' }} />
                    Métricas · Últimos 7 días
                </h2>

                {/* Gráficos históricos */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <ChartCard
                        title="Registros"
                        icon={Users}
                        iconColor="#2284DF"
                        type="line"
                        color="#2284DF"
                        data={metrics?.registrationsPerDay}
                        total={metrics?.registrationsPerDay?.reduce((s, d) => s + d.value, 0)}
                        subtitle="Nuevos usuarios"
                        loading={metricsLoading}
                    />
                    <ChartCard
                        title="Partidas"
                        icon={Swords}
                        iconColor="#FCE503"
                        type="bar"
                        color="#FCE503"
                        data={metrics?.matchesPerDay}
                        total={metrics?.matchesPerDay?.reduce((s, d) => s + d.value, 0)}
                        subtitle="Matches jugados"
                        loading={metricsLoading}
                    />
                    <ChartCard
                        title="Ingresos"
                        icon={CreditCard}
                        iconColor="#22c55e"
                        type="area"
                        color="#22c55e"
                        data={metrics?.revenuePerDay}
                        total={`${metrics?.revenuePerDay?.reduce((s, d) => s + d.value, 0) ?? 0} T`}
                        subtitle="Tickets depositados"
                        formatter={v => `${v} tickets`}
                        loading={metricsLoading}
                    />
                </div>

                {/* Conexiones activas + Top países */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

                    {/* Conexiones en tiempo real */}
                    <div className="rounded-xl p-5 flex flex-col gap-3" style={{ backgroundColor: '#0E2238' }}>
                        <div className="flex items-center justify-between">
                            <span className="flex items-center gap-1.5 text-sm font-semibold text-gray-400">
                                <Radio className="w-4 h-4" style={{ color: '#a855f7' }} />
                                Usuarios Conectados
                                <span className="flex h-2 w-2 relative ml-1">
                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
                                    <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500" />
                                </span>
                            </span>
                            <span className="text-3xl font-bold text-white">
                                {liveConnections?.current ?? '—'}
                            </span>
                        </div>
                        <p className="text-xs text-gray-500 -mt-1">En tiempo real · actualiza con cada conexión</p>
                        {/* Sparkline del historial (últimos 30 min) */}
                        <div className="h-[100px]">
                            {(liveConnections?.history?.length ?? 0) > 1 ? (
                                <ResponsiveContainer width="100%" height="100%">
                                    <AreaChart
                                        data={liveConnections.history.map(h => ({
                                            t: new Date(h.time).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' }),
                                            count: h.count
                                        }))}
                                        margin={{ top: 4, right: 0, left: -40, bottom: 0 }}
                                    >
                                        <XAxis dataKey="t" tick={{ fill: '#6b7280', fontSize: 9 }} axisLine={false} tickLine={false} interval="preserveStartEnd" />
                                        <YAxis tick={{ fill: '#6b7280', fontSize: 9 }} axisLine={false} tickLine={false} allowDecimals={false} />
                                        <Tooltip content={<DarkTooltip formatter={v => `${v} usuarios`} />} />
                                        <Area type="monotone" dataKey="count" stroke="#a855f7" fill="#a855f728" strokeWidth={2} dot={false} />
                                    </AreaChart>
                                </ResponsiveContainer>
                            ) : (
                                <div className="h-full flex items-center justify-center">
                                    <p className="text-xs text-gray-600">El historial se construye cada 30 s desde que el servidor arranca</p>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Top países */}
                    <div className="rounded-xl p-5 flex flex-col gap-3" style={{ backgroundColor: '#0E2238' }}>
                        <span className="flex items-center gap-1.5 text-sm font-semibold text-gray-400">
                            <Globe className="w-4 h-4" style={{ color: '#FCE503' }} />
                            Top Países · Conectados ahora
                        </span>
                        {metricsLoading ? (
                            <div className="flex items-center justify-center h-24">
                                <RefreshCw className="w-5 h-5 animate-spin text-gray-600" />
                            </div>
                        ) : (liveConnections?.topCountries?.length ?? 0) === 0 ? (
                            <div className="flex items-center justify-center h-24">
                                <p className="text-xs text-gray-600">Sin usuarios conectados ahora</p>
                            </div>
                        ) : (
                            <div className="flex flex-col gap-2.5">
                                {(() => {
                                    const max = Math.max(...liveConnections.topCountries.map(c => c.count), 1);
                                    return liveConnections.topCountries.map(({ code, name, count }) => (
                                        <div key={code} className="flex items-center gap-2">
                                            <span className="text-xs text-gray-400 w-24 shrink-0 truncate">{name}</span>
                                            <div className="flex-1 rounded-full overflow-hidden h-2" style={{ backgroundColor: '#122436' }}>
                                                <div
                                                    className="h-full rounded-full transition-all duration-500"
                                                    style={{ width: `${(count / max) * 100}%`, backgroundColor: '#FCE503' }}
                                                />
                                            </div>
                                            <span className="text-xs font-semibold text-white w-6 text-right shrink-0">{count}</span>
                                        </div>
                                    ));
                                })()}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* ── Cola de juego (si hay datos) ──────────────────────────── */}
            {queue && Object.keys(queue).length > 0 && (
                <div className="rounded-xl p-5" style={{ backgroundColor: '#0E2238' }}>
                    <h2 className="font-semibold text-white mb-4 flex items-center gap-2">
                        <Swords className="w-4 h-4" style={{ color: '#a855f7' }} />
                        Estado de Colas
                    </h2>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                        {Object.entries(queue).map(([gameType, stats]) => (
                            <div key={gameType} className="rounded-lg p-3 flex flex-col gap-1" style={{ backgroundColor: '#122436' }}>
                                <p className="text-xs font-semibold text-gray-400 uppercase truncate">{gameType}</p>
                                <p className="text-2xl font-bold text-white">{stats?.currentPlayers ?? 0}</p>
                                <p className="text-xs text-gray-500">
                                    jugadores en cola
                                    {stats?.averageWaitTime > 0 && (
                                        <span className="ml-1 text-gray-600">· ~{stats.averageWaitTime}s</span>
                                    )}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            )}

        </div>
    );
};

export default AdminDashboard;
